/* ============================================================
   Pestle & Mortar — pm-rivo-rewards.js
   Members Edit (rewards) page hydration via RivoJS.
   Six hydration routines + robust init (immediate / event / polling).

   Loaded ONLY from sections/members-edit-hero.liquid so this script
   runs exclusively on /pages/members-edit. Customer-facing data
   surfaces stay readable for guests through the static fallback
   markup in each section.

   Requirements:
     1. Rivo app installed on the store.
     2. Rivo Javascript API enabled (Premium plan).
     3. Customer logged in — guests see the static fallback.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- helpers ---------- */
  function $(sel, root){ return (root || document).querySelector(sel); }
  function fmt(n){ try { return Number(n || 0).toLocaleString('en-IE'); } catch(e){ return String(n || 0); } }
  function shortDate(iso){
    if (!iso) return '';
    try {
      var d = new Date(iso);
      return d.toLocaleDateString('en-IE', { day: '2-digit', month: 'short' });
    } catch(e){ return ''; }
  }
  function escapeHtml(s){
    return String(s == null ? '' : s).replace(/[&<>"']/g, function(c){
      return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];
    });
  }

  /* ---------- shared customer detection ----------
     Three-way state:
       1. No Rivo customer at all → guest view (page default)
       2. Customer detected but not enrolled in Members Edit → JOIN view
       3. Customer detected + enrolled → MEMBER view
     getCustomerPromise() returns the raw customer (or null) for case 1/2/3
     detection. getMemberPromise() is the "enrolled" gate used by downstream
     hydration (rewards, referrals, activity, earned actions) — those
     surfaces only paint for true members. */
  /* Rivo's getCustomerDetails response shape varies by version:
       Some return { success: true, customer: {…} }
       Others return { success: true, first_name: …, points_tally: … } at the root
       Others wrap as { user: {…} } or { profile: {…} }
     This helper picks whichever shape has the expected fields. */
  function resolveCustomer(resp){
    if (!resp) return null;
    var candidates = [resp.customer, resp.user, resp.profile, resp];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (c && typeof c === 'object' && (c.points_tally != null || c.first_name || c.email || c.id)) {
        return c;
      }
    }
    return null;
  }

  var __customerPromise = null;
  function getCustomerPromise(){
    if (__customerPromise) return __customerPromise;
    __customerPromise = window.RivoJS.getCustomerDetails().then(function (resp) {
      console.log('[Rivo] getCustomerDetails response:', resp);
      var c = resolveCustomer(resp);
      if (c) console.log('[Rivo] Shopify customer detected', { isEnrolled: isEnrolledCustomer(c) });
      else   console.log('[Rivo] no Shopify customer — guest view stays everywhere');
      return c;
    }).catch(function (err) {
      console.warn('[Rivo] getCustomerDetails failed:', err);
      return null;
    });
    return __customerPromise;
  }

  // 2-state resolver gate: "has the customer ever earned anything?"
  // Yes → Member view (full dashboard). No → Guest view (anonymous if not
  // signed in, personalized + Join CTA if signed in). The Welcome Bonus
  // click is what creates their first points_logs entry, flipping them
  // from personalized-guest to Member on next load.
  /* Local "has explicitly clicked Join Members Edit" flag.
     Rivo auto-credits customers with the Welcome Bonus on signup, so we can't
     rely on Rivo's enrollment status alone — that flips every fresh customer
     to the Member view before they've consciously joined. We gate the Member
     view on this localStorage flag, which is only set after a successful click
     of the JOIN button on this device. */
  var JOIN_FLAG_KEY = 'pm:members-edit-joined';
  function hasClickedJoin(){
    try { return localStorage.getItem(JOIN_FLAG_KEY) === '1'; } catch(_) { return false; }
  }
  function markClickedJoin(){
    try { localStorage.setItem(JOIN_FLAG_KEY, '1'); } catch(_) {}
  }

  function isEnrolledCustomer(c){
    if (!c) return false;
    // HYBRID: tag OR (localStorage + Rivo activity).
    //
    // 1) PRIMARY — Shopify customer tag `members-edit-member`.
    //    Set by the Flow webhook after a successful JOIN click. Per-account,
    //    cross-device. Single source of truth once it lands (~30-60s post-click).
    var tags = window.__shopifyCustomerTags;
    if (Array.isArray(tags) && tags.indexOf('members-edit-member') !== -1) return true;
    if (typeof tags === 'string' && tags.indexOf('members-edit-member') !== -1) return true;
    //
    // 2) BRIDGE — localStorage flag AND Rivo activity. Both required, so
    //    we never false-promote:
    //      • localStorage alone (Rivo never registered) → still NOT member
    //      • Rivo activity alone (auto-enroll, no JOIN click) → still NOT member
    //    Only when the user explicitly clicked AND Rivo has any activity
    //    do we flip to Member — covers the ~30-60s window before the tag
    //    webhook lands.
    if (!hasClickedJoin()) return false;
    var logs = c.points_logs || c.completed_actions || [];
    if (Array.isArray(logs) && logs.length > 0) return true;
    var lifetime = Number(c.lifetime_earnings_tally || c.lifetime_points || 0);
    if (lifetime > 0) return true;
    var tally = Number(c.points_tally || c.points_balance || c.points_total || 0);
    return tally > 0;
  }

  function getMemberPromise(){
    // Backward compat: downstream hydration uses this as the "enrolled" gate.
    return getCustomerPromise().then(function(c){
      if (isEnrolledCustomer(c)) {
        window.__pmIsRivoMember = true;
        return c;
      }
      return null;
    });
  }

  /* ---------- 1. CUSTOMER + POINTS + TIER (hero card) ---------- */
  function paintPoints(pts){
    if (pts == null) return;
    var txt = (typeof pts === 'string') ? pts : fmt(pts);
    console.log('[Rivo] painting points balance:', txt);
    document.querySelectorAll('.rivo-points-balance').forEach(function (el) {
      el.textContent = txt;
    });
    var navPill = document.getElementById('rivo-nav-points');
    if (navPill) navPill.removeAttribute('hidden');
    window.__pmPoints = (typeof pts === 'number') ? pts : (parseInt(String(pts).replace(/[^\d-]/g, ''), 10) || 0);
    paintRewardAffordability();
  }

  function hydrateCustomerAndTier(){
    console.log('[Rivo] hydrateCustomerAndTier started');

    getCustomerPromise().then(function (c) {
      var guestCard  = document.getElementById('me-card-guest');
      var joinCard   = document.getElementById('me-card-join');
      var memberCard = document.getElementById('me-card-member');
      var guestCtaRow  = document.getElementById('cta-row-guest');
      var joinCtaRow   = document.getElementById('cta-row-join');
      var memberCtaRow = document.getElementById('cta-row-member');

      // 2-state resolver gated on points history.
      // - Not signed in to Shopify → anonymous Guest (default state, no change).
      // - Signed in but no points history → personalized Guest (Welcome back + Join CTA).
      // - Signed in WITH points history → Member.
      if (!window.__shopifyCustomerId) {
        document.body.classList.add('view-guest');
        return; // anonymous guest, page stays as-is
      }

      // Signed in. Differentiate via points history.
      document.body.classList.add('is-signed-in');

      if (!isEnrolledCustomer(c)) {
        /* ---------- PERSONALIZED GUEST (signed in, no history) ----------
           Same visual treatment as the legacy JOIN state — "Welcome back, X"
           + Join CTA + "+250 waiting" sidebar card. Body carries
           view-guest + is-signed-in for any future CSS that wants to target
           the personalized variant. */
        console.log('[Rivo] customer signed in, no points history → showing personalized guest view');
        document.body.classList.remove('is-guest');
        document.body.classList.remove('is-member');   // strip server-set member class
        document.body.classList.remove('view-member'); // strip server-set member view
        document.body.classList.add('is-join');     // legacy hook (existing CSS)
        document.body.classList.add('view-guest');  // new model
        document.querySelectorAll('[data-pm-guest-only]').forEach(function(el){ el.setAttribute('hidden', ''); });
        document.querySelectorAll('[data-pm-join-only]').forEach(function(el){ el.removeAttribute('hidden'); });
        document.querySelectorAll('[data-pm-member-only]').forEach(function(el){ el.setAttribute('hidden', ''); });
        if (guestCard)    guestCard.setAttribute('hidden', '');
        if (joinCard)     joinCard.removeAttribute('hidden');
        if (memberCard)   memberCard.setAttribute('hidden', '');
        if (guestCtaRow)  guestCtaRow.setAttribute('hidden', '');
        if (joinCtaRow)   joinCtaRow.removeAttribute('hidden');
        if (memberCtaRow) memberCtaRow.setAttribute('hidden', '');

        // Personalise: first name on card + eyebrow. Prefer the Liquid-rendered
        // Shopify name (always present when signed in); fall back to Rivo's
        // record if it returned anything, then to email-prefix as a last resort.
        var firstName = (window.__shopifyCustomerFirstName
          || (c && c.first_name)
          || (c && c.email ? c.email.split('@')[0] : '')
          || (window.__shopifyCustomerEmail ? window.__shopifyCustomerEmail.split('@')[0] : '')
          || '').trim();
        var joinNameEl = document.getElementById('rivo-join-name');
        if (joinNameEl && firstName) joinNameEl.textContent = firstName;
        var joinEy = document.getElementById('rivo-join-ey');
        if (joinEy && firstName) joinEy.textContent = joinEy.textContent + ', ' + firstName;
        return; // skip member-only hydration
      }

      /* ---------- MEMBER (has points history) ---------- */
      document.body.classList.remove('is-guest');
      document.body.classList.remove('is-join');
      document.body.classList.remove('view-guest');
      document.body.classList.add('is-member');
      document.body.classList.add('view-member');

      document.querySelectorAll('[data-pm-guest-only]').forEach(function(el){ el.setAttribute('hidden', ''); });
      document.querySelectorAll('[data-pm-join-only]').forEach(function(el){ el.setAttribute('hidden', ''); });
      document.querySelectorAll('[data-pm-member-only]').forEach(function(el){ el.removeAttribute('hidden'); });

      if (guestCard)    guestCard.setAttribute('hidden', '');
      if (joinCard)     joinCard.setAttribute('hidden', '');
      if (memberCard)   memberCard.removeAttribute('hidden');
      if (guestCtaRow)  guestCtaRow.setAttribute('hidden', '');
      if (joinCtaRow)   joinCtaRow.setAttribute('hidden', '');
      if (memberCtaRow) memberCtaRow.removeAttribute('hidden');

      console.log('[Rivo] customer object keys:', Object.keys(c));

      // Hero — points balance. Try every shape Rivo might return.
      var pointsText = c.pretty_points_tally
        || (c.points_tally != null ? fmt(c.points_tally) : null)
        || (c.points_balance != null ? fmt(c.points_balance) : null)
        || (c.points != null ? fmt(c.points) : null);

      // Cache last-known member state so subsequent page loads can
      // pre-render the Member card before Rivo SDK finishes loading —
      // eliminates the "guest card flash" on returning visits.
      try {
        if (window.__shopifyCustomerId && pointsText) {
          localStorage.setItem('pm:meCardState:' + window.__shopifyCustomerId, JSON.stringify({
            state: 'member',
            points: pointsText,
            savedAt: Date.now()
          }));
        }
      } catch(_){}

      if (pointsText) {
        paintPoints(pointsText);
      } else if (typeof window.RivoJS.getCustomerPoints === 'function') {
        console.warn('[Rivo] customer object has no points field — falling back to getCustomerPoints()');
        window.RivoJS.getCustomerPoints().then(function (pResp) {
          console.log('[Rivo] getCustomerPoints response:', pResp);
          var pts = pResp && (pResp.pretty_points_tally || pResp.points_tally || pResp.points_balance || pResp.points);
          paintPoints(pts);
        }).catch(function (err) {
          console.warn('[Rivo] getCustomerPoints failed:', err);
        });
      }

      // Hero — name + member since
      var who = $('#rivo-who');
      if (who && (c.first_name || c.email)) {
        var name = c.first_name
          ? (c.first_name + (c.last_name ? ' ' + c.last_name.charAt(0) + '.' : ''))
          : c.email.split('@')[0];
        who.textContent = 'Account · ' + name;
      }
      var since = $('#rivo-since');
      if (since && c.account_activated_at) {
        since.textContent = 'Member since ' + new Date(c.account_activated_at).getFullYear();
      }

      // Chain the tier hydration only after membership is confirmed.
      return window.RivoJS.getCustomerVipTier();
    }).then(function (resp) {
      if (!resp) return; // chain skipped (not a member) or no tier response
      console.log('[Rivo] getCustomerVipTier response:', resp);
      if (!resp.success) {
        console.warn('[Rivo] getCustomerVipTier not successful:', resp);
        return;
      }
      var tier     = resp.tier || {};
      var nextTier = resp.next_tier || null;

      // Expose tier name globally so birthday modal + future features can
      // branch on tier without re-fetching.
      if (tier.name) window.PMTier = String(tier.name);

      // Tier line + position
      var tierLine = $('#rivo-tier-line');
      if (tierLine && tier.name) tierLine.textContent = 'Tier · ' + tier.name + ' Member';

      // Position pill (Tier N of M) — derive from tier order on the page
      var tiers = Array.prototype.slice.call(document.querySelectorAll('#rivo-tiers-grid .tier'));
      var idx   = tiers.findIndex(function (t) {
        return (t.dataset.tierName || '').toLowerCase() === String(tier.name || '').toLowerCase();
      });
      var pos = $('#rivo-tier-pos');
      if (pos && idx >= 0) {
        pos.textContent = 'Tier ' + String(idx + 1).padStart(2, '0') + ' of ' + String(tiers.length).padStart(2, '0');
      }

      // Mark current tier in the Tiers section
      tiers.forEach(function (t) { t.classList.remove('is-current'); });
      if (idx >= 0) tiers[idx].classList.add('is-current');

      // Milestones — bold the tiers achieved
      var milestones = document.querySelectorAll('#rivo-milestones span');
      milestones.forEach(function (m, i) {
        m.classList.toggle('hit', i <= idx);
      });

      // Progress bar fill + "next" copy
      var fill = $('#rivo-fill');
      var next = $('#rivo-next');
      if (resp.highest_tier) {
        if (fill) fill.style.width = '100%';
        if (next) next.innerHTML = "You're at our highest tier — enjoy every perk.";
      } else if (nextTier) {
        var current = tier.threshold || 0;
        var target  = nextTier.threshold || 0;
        var toGo    = nextTier.to_spend_or_earn || 0;
        var earned  = Math.max(target - toGo, 0);
        var pct     = target > current
          ? Math.min(100, Math.max(0, Math.round((earned - current) / (target - current) * 100)))
          : 0;
        if (fill) fill.style.width = pct + '%';
        if (next) {
          next.innerHTML = resp.next_tier_desc
            || ('Earn ' + fmt(toGo) + ' more points to unlock <strong>' + escapeHtml(nextTier.name) + ' Member</strong>.');
        }
      }
    }).catch(function (err) {
      console.warn('[Rivo] getCustomerVipTier failed:', err);
    });
  }

  /* ---------- 2. REWARDS CATALOG ---------- */
  function rewardTypeAndLabel(r){
    // Map Rivo reward_type to our visual category
    var rt = (r.reward_type || '').toLowerCase();
    if (rt.indexOf('product') !== -1 || rt === 'free_product') {
      return { type: 'gift', label: 'Complimentary' };
    }
    return { type: 'money', label: 'Money off' };
  }

  function paintRewardAffordability(){
    var pts = window.__pmPoints || 0;
    document.querySelectorAll('#rivo-rewards-grid .reward[data-reward-id]').forEach(function (card) {
      var cost = parseInt(card.dataset.cost || '0', 10);
      var btn  = card.querySelector('.redeem');
      if (cost > pts) {
        card.classList.add('locked');
        if (btn) btn.textContent = fmt(cost - pts) + ' points away';
        if (btn) btn.disabled = true;
      } else {
        card.classList.remove('locked');
        if (btn && btn.textContent && btn.textContent.indexOf('points away') !== -1) {
          btn.textContent = 'Redeem reward';
        }
        if (btn) btn.disabled = false;
      }
    });
  }

  function hydrateRewards(){
    console.log('[Rivo] hydrateRewards started');
    getMemberPromise().then(function (member) {
      if (!member) return; // guest stays — leave the static fallback catalog
      return window.RivoJS.getShopRewards();
    }).then(function (resp) {
      if (!resp) return;
      console.log('[Rivo] getShopRewards response:', resp);
      // getShopRewards returns either an Array directly OR { rewards: Array } / { data: Array }.
      // Some SDK versions also flip success:false alongside valid data — don't gate on it.
      var rewards = Array.isArray(resp) ? resp
                  : (resp && Array.isArray(resp.rewards) ? resp.rewards
                  : (resp && Array.isArray(resp.data) ? resp.data : []));
      if (!rewards.length) {
        console.warn('[Rivo] no rewards returned — leaving static fallback in place');
        return;
      }
      var grid = $('#rivo-rewards-grid');
      if (!grid) return;

      // Keep the tier-perk cards (they're benefits, not points purchasable)
      var perks = Array.prototype.slice.call(grid.querySelectorAll('.reward[data-type="perk"]'));

      // Remove the static money/gift placeholders
      grid.querySelectorAll('.reward:not([data-type="perk"])').forEach(function (el) { el.remove(); });

      // Sort rewards by points_amount ascending
      rewards = rewards.slice().sort(function (a, b) {
        return (a.points_amount || 0) - (b.points_amount || 0);
      });

      rewards.forEach(function (r) {
        var cat = rewardTypeAndLabel(r);
        var card = document.createElement('div');
        card.className = 'reward';
        card.dataset.type = cat.type;
        card.dataset.rewardId = r.id;
        card.dataset.cost = r.points_amount || 0;

        card.innerHTML =
          '<span class="typ' + (cat.type === 'gift' ? ' gift' : '') + '">' + escapeHtml(cat.label) + '</span>' +
          '<p class="pts">' + fmt(r.points_amount) + ' <small>points</small></p>' +
          '<h4 class="nm">' + escapeHtml(r.name) + '</h4>' +
          '<p class="desc">' + escapeHtml(r.pretty_display_rewards || r.name) + '</p>' +
          '<button class="redeem" type="button">Redeem reward</button>';

        // Insert before the first perk so perks stay at the end of the grid
        if (perks.length) grid.insertBefore(card, perks[0]);
        else grid.appendChild(card);
      });

      // Redeem buttons are wired via a single delegated handler on
      // #rivo-rewards-grid (see CATALOG REDEEM IIFE at end of file) — that
      // covers both the dynamic cards painted above and any future static
      // card carrying data-reward-id.
      paintRewardAffordability();
    }).catch(function (err) {
      console.warn('[Rivo] getShopRewards failed:', err);
    });
  }

  /* ---------- 3. REFERRALS ---------- */
  function hydrateReferrals(){
    console.log('[Rivo] hydrateReferrals started');
    getMemberPromise().then(function (member) {
      if (!member) return; // guest stays — leave the static refer fallback

      window.RivoJS.getCustomerReferrals().then(function (resp) {
        console.log('[Rivo] getCustomerReferrals response:', resp);
        if (!resp) return;
        var input = $('#rivo-ref-url');
        var msg   = $('#rivo-ref-msg-url');
        if (input && resp.url) input.value = resp.url;
        if (msg   && resp.url) msg.textContent = resp.url.replace(/^https?:\/\//, '');
      }).catch(function (err) {
        console.warn('[Rivo] getCustomerReferrals failed:', err);
      });

      window.RivoJS.getCustomerReferralStats().then(function (s) {
        console.log('[Rivo] getCustomerReferralStats response:', s);
        if (!s) return;
        var c  = $('#rivo-ref-clicked');
        var cl = $('#rivo-ref-claimed');
        var cp = $('#rivo-ref-completed');
        if (c)  c.textContent  = fmt(s.clicked_count   || 0);
        if (cl) cl.textContent = fmt(s.claimed_count   || 0);
        if (cp) cp.textContent = fmt(s.completed_count || 0);
      }).catch(function (err) {
        console.warn('[Rivo] getCustomerReferralStats failed:', err);
      });

      var copyBtn = $('#rivo-ref-copy');
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          var input = $('#rivo-ref-url');
          if (!input || !input.value) return;
          input.select();
          try { document.execCommand('copy'); } catch (e) {}
          if (navigator.clipboard) navigator.clipboard.writeText(input.value);
          var t = copyBtn.textContent;
          copyBtn.textContent = 'Copied!';
          setTimeout(function () { copyBtn.textContent = t; }, 1500);
        });
      }
    });
  }

  /* ---------- 4. ACTIVITY (points logs) ---------- */
  function hydrateActivity(){
    console.log('[Rivo] hydrateActivity started');
    var list = $('#rivo-activity-list');
    if (!list) return;
    getMemberPromise().then(function (member) {
      if (!member) return; // guest stays — leave the static activity rows
      return window.RivoJS.getCustomerPointsLogs();
    }).then(function (resp) {
      if (!resp) return;
      console.log('[Rivo] getCustomerPointsLogs response:', resp);
      // Rivo's response shape can vary; accept any array we find.
      var logs = (resp && resp.points_logs) || (resp && resp.logs) || (Array.isArray(resp) ? resp : null);
      if (!logs) {
        console.warn('[Rivo] no points_logs array in response — leaving DOM as-is');
        return;
      }
      // We have an authoritative response — replace the static demo rows
      // even if the array is empty, so signed-in members don't see fake history.
      list.innerHTML = '';
      if (!logs.length) {
        list.innerHTML = '<div class="row" style="grid-template-columns:1fr;padding:24px 0;color:var(--grey-1);font-size:13px;line-height:1.6">Your points activity will appear here as you earn and redeem.</div>';
        return;
      }
      logs.slice(0, 10).forEach(function (log) {
        var diff   = log.points_diff != null ? log.points_diff : (log.points_amount != null ? log.points_amount : 0);
        var earned = diff >= 0;
        var row = document.createElement('div');
        row.className = 'row';
        row.innerHTML =
          '<span class="d">' + escapeHtml(shortDate(log.applied_at || log.created_at)) + '</span>' +
          '<div class="desc"><strong>' + escapeHtml(log.external_note || log.note || (earned ? 'Points earned' : 'Points redeemed')) + '</strong>' +
            '<span>' + (earned ? 'Added to your balance' : 'Spent from your balance') + '</span></div>' +
          '<span class="typ ' + (earned ? 'earned">Earned' : 'spent">Redeemed') + '</span>' +
          '<span class="pts ' + (earned ? '' : 'neg') + '">' + (earned ? '+' : '−') + fmt(Math.abs(diff)) + '</span>';
        list.appendChild(row);
      });
    }).catch(function (err) {
      console.warn('[Rivo] getCustomerPointsLogs failed:', err);
    });
  }

  /* ---------- 5. "See full history" — open Rivo's widget on My Rewards ---------- */
  function wireOpenWidget(){
    var openLink = $('#rivo-open-widget');
    if (!openLink) return;
    openLink.addEventListener('click', function (e) {
      e.preventDefault();
      if (window.RivoJS && window.RivoJS.openWidget) {
        window.RivoJS.openWidget({ page: 'my_rewards' });
      }
    });
  }

  /* ---------- 6. "Review a product" — deep-link to most recent unreviewed PDP ----------
     Whole-card click target (the card is now an <a> with href="#") plus the
     inner CTA span fall back to the same handler. */
  function wireReviewDeepLink(){
    var card = $('#earn-review');
    var cta  = $('#earn-review-cta');
    var trigger = card || cta;
    if (!trigger) return;

    trigger.addEventListener('click', function (e) {
      e.preventDefault();

      // (a) If Liquid pre-rendered a last unreviewed product URL on the card, use it.
      var preset = card && card.getAttribute('data-last-product-url');
      if (preset) {
        var sep = preset.indexOf('?') === -1 ? '?' : '&';
        window.location.href = preset + sep + 'openReview=1';
        return;
      }

      // (b) If RivoJS exposes the customer + we can fetch order JSON, try that.
      if (window.RivoJS && window.RivoJS.getCustomerDetails) {
        window.RivoJS.getCustomerDetails().then(function (resp) {
          var c = resolveCustomer(resp);
          if (!c || !c.orders_count) {
            // No orders yet — route them to shop the bestsellers instead.
            window.location.href = '/collections/bestsellers';
            return;
          }
          // Shopify's customer account JSON endpoint — works for logged-in customers.
          fetch('/account/orders.json', { credentials: 'same-origin' })
            .then(function(r){ return r.ok ? r.json() : null; })
            .then(function(data){
              var orders = (data && data.orders) || [];
              var pdpUrl = null;
              for (var i = 0; i < orders.length && !pdpUrl; i++) {
                var lines = orders[i].line_items || [];
                for (var j = 0; j < lines.length && !pdpUrl; j++) {
                  if (lines[j].product_url) pdpUrl = lines[j].product_url;
                  else if (lines[j].handle) pdpUrl = '/products/' + lines[j].handle;
                }
              }
              if (pdpUrl) {
                var sep2 = pdpUrl.indexOf('?') === -1 ? '?' : '&';
                window.location.href = pdpUrl + sep2 + 'openReview=1';
              } else {
                window.location.href = '/account';
              }
            })
            .catch(function(){ window.location.href = '/account'; });
        }).catch(function(){ window.location.href = '/account'; });
        return;
      }

      // (c) Final fallback — send them to their account page.
      window.location.href = '/account';
    });
  }

  /* ---------- INIT ----------
     Three ways RivoJS might become available:
       1. Already on the page (script ran before us).
       2. The `rivo-js-loaded` event fires.
       3. We poll window.RivoJS every 250ms for up to 15 seconds.
     didInit guard prevents double-firing. */
  var didInit = false;
  function init(){
    if (didInit) return;
    if (!window.RivoJS) { console.warn('[Rivo] init called but window.RivoJS is missing'); return; }
    didInit = true;
    console.log('[Rivo] Initialising Members Edit hydration');
    try { hydrateCustomerAndTier(); } catch(e){ console.warn('[Rivo] customer/tier', e); }
    try { hydrateRewards();         } catch(e){ console.warn('[Rivo] rewards', e); }
    try { hydrateReferrals();       } catch(e){ console.warn('[Rivo] referrals', e); }
    try { hydrateActivity();        } catch(e){ console.warn('[Rivo] activity', e); }
    try { wireOpenWidget();         } catch(e){ console.warn('[Rivo] widget', e); }
    try { wireReviewDeepLink();     } catch(e){ console.warn('[Rivo] review link', e); }
    try { markEarnedActions();      } catch(e){ console.warn('[Rivo] earned-state', e); }
    try { wireEnrollCta();          } catch(e){ console.warn('[Rivo] enroll cta', e); }
  }

  /* Optimistic Member-view paint — fired immediately after a successful
     JOIN credit so the customer sees their +250 / Bronze tier without
     waiting for a page reload + Rivo re-fetch. hydrateCustomerAndTier()
     runs in the background and will overwrite these placeholders with
     authoritative live Rivo data. */
  function paintJoinedMemberOptimistically(initial){
    initial = initial || {};
    var points = initial.points != null ? String(initial.points) : '250';
    var tier   = initial.tier   || 'Bronze';

    // Body class swap
    document.body.classList.remove('is-guest');
    document.body.classList.remove('is-join');
    document.body.classList.remove('view-guest');
    document.body.classList.add('is-member');
    document.body.classList.add('view-member');

    // Hero text swap (guest/join → member)
    document.querySelectorAll('[data-pm-guest-only]').forEach(function(el){ el.setAttribute('hidden', ''); });
    document.querySelectorAll('[data-pm-join-only]').forEach(function(el){ el.setAttribute('hidden', ''); });
    document.querySelectorAll('[data-pm-member-only]').forEach(function(el){ el.removeAttribute('hidden'); });

    // Card swap
    var guestCard  = document.getElementById('me-card-guest');
    var joinCard   = document.getElementById('me-card-join');
    var memberCard = document.getElementById('me-card-member');
    if (guestCard)  guestCard.setAttribute('hidden', '');
    if (joinCard)   joinCard.setAttribute('hidden', '');
    if (memberCard) memberCard.removeAttribute('hidden');

    // CTA row swap
    var guestCtaRow  = document.getElementById('cta-row-guest');
    var joinCtaRow   = document.getElementById('cta-row-join');
    var memberCtaRow = document.getElementById('cta-row-member');
    if (guestCtaRow)  guestCtaRow.setAttribute('hidden', '');
    if (joinCtaRow)   joinCtaRow.setAttribute('hidden', '');
    if (memberCtaRow) memberCtaRow.removeAttribute('hidden');

    // Paint points + name immediately
    document.querySelectorAll('.rivo-points-balance').forEach(function(el){
      el.textContent = points;
    });
    var firstName = (window.__shopifyCustomerFirstName || '').trim();
    var who = document.getElementById('rivo-who');
    if (who && firstName) who.textContent = firstName;

    // Tier hint (will be replaced by hydrate)
    var tierLine = document.getElementById('rivo-tier-line');
    if (tierLine) tierLine.textContent = 'Tier · ' + tier + ' Member';
    var tierPos = document.getElementById('rivo-tier-pos');
    if (tierPos) tierPos.textContent = 'Tier 01 of 03';

    // Scroll back to the card so the customer SEES the celebration
    if (memberCard) {
      try { memberCard.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_){}
    }
  }

  /* ---------- 8. JOIN BUTTON — credit the 250 Welcome Bonus ----------
     The customer is already enrolled in Rivo at the API level (because
     "All customers eligible" is on in admin). The Join click just credits
     the Welcome Bonus custom action, which creates a points_logs entry,
     which flips the page from personalized-guest to Member on next load.
     https://developer-docs.rivo.io/docs/trigger-custom-actions-with-javascript */
  function joinMembersEdit(btn){
    if (!window.Rivo || !window.Rivo.global_config || !window.Rivo.common || !window.Rivo.common.customer) {
      console.warn('[Join] Rivo runtime not loaded');
      return;
    }
    if (btn) {
      btn.disabled = true;
      btn.dataset.originalLabel = btn.textContent;
      btn.textContent = 'Joining…';
    }

    var proxyUrl = window.Rivo.global_config.proxy_paths.loy
                 + '/loy/customers/' + window.Rivo.common.customer.id
                 + '/record_custom_action';

    return fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ custom_action_name: 'Welcome Bonus' })
    }).then(function(r){
      if (!r.ok) {
        var err = new Error('HTTP ' + r.status);
        err.status = r.status;
        throw err;
      }
      return r.json().catch(function(){ return {}; });
    }).then(function(){
      console.log('[Join] +250 welcome points credited');

      // Mark this device as having clicked Join so isEnrolledCustomer
      // flips the page to Member view on next load.
      markClickedJoin();

      // Fire Klaviyo event for the welcome-email flow
      fireKlaviyoJoinEvent();

      // Fire Shopify Flow webhook to tag the customer with 'members-edit-member'.
      // Server-side Flow handles the tag mutation (storefront JS can't tag directly).
      tagCustomerViaFlowWebhook();

      if (window.PMCelebrate) {
        window.PMCelebrate({
          points: 250,
          reason: 'Welcome to The Members Edit',
          detail: '+250 welcome points credited to your account'
        });
      }

      // Optimistic paint — we already know what this customer just earned,
      // so flip the page to Member view immediately. No reload, no 2.5s
      // wait, no second Rivo SDK fetch. hydrateCustomerAndTier() runs in
      // the background to reconcile with the live Rivo data (it will
      // overwrite our placeholders if anything differs).
      paintJoinedMemberOptimistically({
        points: 250,
        tier: 'Bronze'
      });
      try { hydrateCustomerAndTier(); } catch(_) {}

      // Auto-refresh ~2s after JOIN so the server re-renders with the new
      // `members-edit-member` tag (lands via Flow webhook) + the Member view
      // shows everywhere on the page (account hero card, sidebar, etc.).
      setTimeout(function(){ window.location.reload(); }, 2000);
    }).catch(function(err){
      console.warn('[Join] credit failed', err);
      if (btn) {
        btn.disabled = false;
        btn.textContent = btn.dataset.originalLabel || 'Join · 250 points';
      }
      // 4xx from Rivo = validation rejection (most commonly "Welcome Bonus
      // already credited" for an existing member). Silently treat as a
      // successful no-op and flip the page to Member view rather than
      // alarming the customer with the "Sorry, we couldn't complete your
      // sign-up" alert. 5xx + network errors still surface the alert.
      if (err && err.status >= 400 && err.status < 500) {
        console.log('[Join] non-fatal ' + err.status + ' — treating as already-enrolled');
        markClickedJoin();
        try { hydrateCustomerAndTier(); } catch(_) {}
        return;
      }
      alert("Sorry, we couldn't complete your sign-up. Please try again in a moment.");
    });
  }

  function fireKlaviyoJoinEvent(){
    if (!window.klaviyo || typeof window.klaviyo.push !== 'function') {
      window.klaviyo = window.klaviyo || [];
    }
    window.klaviyo.push(['identify', {
      '$email':      window.__shopifyCustomerEmail || '',
      '$first_name': window.__shopifyCustomerFirstName || ''
    }]);
    window.klaviyo.push(['track', 'Joined Members Edit', {
      source: 'rewards_page',
      welcome_points: 250,
      enrolled_at: new Date().toISOString()
    }]);
    console.log('[Join] Klaviyo event fired: Joined Members Edit');
  }

  /* ---------- Shopify Flow webhook — tag the customer with 'members-edit-member'.
     Storefront JS can't mutate customer tags directly (admin scope required), so we
     POST to a Shopify Flow webhook trigger which runs server-side and applies the tag.
     The Flow's Advanced Settings expects two variables: fieldOne=customerId, fieldTwo=email.
     If the webhook has Static Token auth enabled, set FLOW_WEBHOOK_TOKEN below. */
  var FLOW_WEBHOOK_URL   = 'https://flow-webhooks.k8s.eu.codecreationlabs.cloud/webhook/xbb0se99';
  var FLOW_WEBHOOK_TOKEN = '352a1q3d4q5v024r5o0l1c1c5v2e0h2a1i1m5a031n4e0c2o7g3o3t4q193q1a6d';

  function tagCustomerViaFlowWebhook(){
    var customerId = window.__shopifyCustomerId;
    var email      = window.__shopifyCustomerEmail;
    if (!customerId) {
      console.warn('[Join→Tag] no customer id — skipping tag webhook');
      return;
    }
    var headers = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
    if (FLOW_WEBHOOK_TOKEN) headers['X-Api-Key'] = FLOW_WEBHOOK_TOKEN;

    fetch(FLOW_WEBHOOK_URL, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ customerId: customerId, email: email || '' })
    }).then(function(r){
      if (!r.ok) throw new Error('HTTP ' + r.status);
      console.log('[Join→Tag] Flow webhook fired — customer should be tagged within ~1 min');
    }).catch(function(err){
      console.warn('[Join→Tag] Flow webhook failed:', err);
    });
  }

  // Wire delegated clicks for any Join trigger on the page — runs once.
  var __enrollWired = false;
  function wireEnrollCta(){
    if (__enrollWired) return;
    __enrollWired = true;
    document.addEventListener('click', function(e){
      var trigger = e.target.closest('[data-rivo-join], #rivo-enroll-cta, .join-trigger');
      if (!trigger) return;
      e.preventDefault();
      joinMembersEdit(trigger);
    });
  }

  /* ---------- AUTO-CLAIM after registration/sign-in ----------
     When a guest clicks "Join free · 250 points" on the Members Edit hero,
     we redirect them to /account/register (or /account/login) with a return_url
     query that brings them back to /pages/members-edit?claim_welcome=1.
     This block detects that flag on page load and fires joinMembersEdit() once
     the customer is signed in + Rivo SDK has loaded — so they don't need to
     click JOIN a second time after registering. */
  function autoClaimIfFlagged(){
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('claim_welcome') !== '1') return;
      if (!window.__shopifyCustomerId) {
        // Guest still — they didn't actually sign in. Leave the flag alone so
        // they can try again from the JOIN button when registered.
        return;
      }
      // Wait until Rivo SDK + customer object are ready, then auto-fire JOIN.
      var attempts = 0;
      var poll = setInterval(function(){
        attempts++;
        var ready = window.Rivo && window.Rivo.global_config && window.Rivo.common && window.Rivo.common.customer && window.Rivo.common.customer.id;
        if (ready) {
          clearInterval(poll);
          // Strip the query param so refreshes don't re-fire.
          try {
            var url = new URL(window.location.href);
            url.searchParams.delete('claim_welcome');
            window.history.replaceState({}, '', url.toString());
          } catch(_){}

          // If the customer already claimed the Welcome Bonus (i.e. they are
          // already an enrolled Rivo member with points history), DON'T fire
          // joinMembersEdit() — Rivo will reject the duplicate and trigger
          // the scary "Sorry, we couldn't complete your sign-up" alert.
          // Just mark the local flag so the page treats them as a Member.
          var c = window.Rivo.common.customer;
          var alreadyMember = false;
          var hasPointsLog = c.points_logs && c.points_logs.length > 0;
          var hasBalance   = Number(c.points_tally || c.points_balance || c.lifetime_earnings_tally || 0) > 0;
          if (hasPointsLog || hasBalance) alreadyMember = true;
          if (alreadyMember) {
            console.log('[Join] auto-claim skipped — customer is already an enrolled member');
            markClickedJoin();
            // Re-run the resolver so the Member view paints without a reload.
            try { hydrateCustomerAndTier(); } catch(_) {}
            return;
          }

          joinMembersEdit(null);
        } else if (attempts >= 40) {
          clearInterval(poll);
          console.warn('[Join] auto-claim aborted — Rivo SDK not ready after 10s');
        }
      }, 250);
    } catch(err){
      console.warn('[Join] auto-claim error:', err);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoClaimIfFlagged);
  } else {
    autoClaimIfFlagged();
  }

  /* ---------- 7. "Earned" state — mark one-time actions the member has completed ----------
     Reads points_logs and adds .is-earned to the matching earn card so it
     renders dimmed with a green ✓ EARNED badge. Recurring actions (purchase,
     birthday, review, refer, etc.) are deliberately excluded.

     The action_key strings must match what Rivo emits in the points-log
     "action_key" field. Verify in Rivo admin → Earn actions → API key
     when wiring up new earn actions. */
  /* ============================================================
     EARN CARD MATCHING — three layers, each more forgiving than the last.
     When Rivo's external_note matches an entry below, we look for a card to flip:
       1. [data-earn-action="signup"]        ← most explicit, engineer-set
       2. #earn-join                          ← ID-based, legacy & explicit
       3. <h4> text matches titleRx           ← zero-config, works for any new card
     To add a new earn action: append an entry to ACTION_MATCHERS. As long as
     the card's title contains the keyword, no further JS or markup change is
     required.
     ============================================================ */
  var ACTION_MATCHERS = [
    { name: 'signup',    dataAction: 'signup',    id: '#earn-join',      noteRx: /sign\s*up|signup|join(\s|ed)|welcome\s*bonus|account\s*created/i, titleRx: /join.*members.*edit|welcome.*members/i },
    { name: 'skin_quiz', dataAction: 'skin_quiz', id: '#earn-quiz',      noteRx: /skin\s*quiz|quiz\s*completed|completed\s*quiz/i,                  titleRx: /skin\s*quiz/i },
    { name: 'email_sub', dataAction: 'email_sub', id: '#earn-email-cta', noteRx: /email\s*sub|newsletter|subscribed\s*to\s*email/i,                  titleRx: /subscribe.*email|email\s*sub/i },
    { name: 'sms_sub',   dataAction: 'sms_sub',   id: '#earn-sms',       noteRx: /sms\s*sub|text\s*marketing|subscribed\s*to\s*sms/i,                titleRx: /subscribe.*sms|sms\s*sub/i },
    { name: 'instagram', dataAction: 'Follow on Instagram', id: '#earn-ig', noteRx: /instagram|insta(\s|gram)|@pestleandmortar.*instagram/i,            titleRx: /instagram/i },
    { name: 'tiktok',    dataAction: 'Follow on TikTok',    id: '#earn-tt', noteRx: /tiktok|tik\s*tok/i,                                                titleRx: /tiktok/i },
    { name: 'facebook',  dataAction: 'Follow on Facebook',  id: '#earn-fb', noteRx: /facebook|liked\s*facebook/i,                                       titleRx: /facebook/i }
    // ADD NEW EARN ACTIONS HERE (e.g. Pinterest, YouTube, Threads):
    // { name: 'pinterest', dataAction: 'pinterest', id: '#earn-pin', noteRx: /pinterest/i, titleRx: /pinterest/i }
  ];

  function findEarnCard(matcher){
    // Layer 1 — data-earn-action attribute (most explicit)
    if (matcher.dataAction) {
      var byData = document.querySelector('.earn .e[data-earn-action="' + matcher.dataAction + '"]');
      if (byData) return byData;
    }
    // Layer 2 — ID selector
    if (matcher.id) {
      var byId = document.querySelector(matcher.id);
      if (byId) return byId;
    }
    // Layer 3 — h4 title regex (zero-config fallback)
    if (matcher.titleRx) {
      var allCards = document.querySelectorAll('section.earn .e, .earn .grid .e');
      for (var i = 0; i < allCards.length; i++) {
        var h4 = allCards[i].querySelector('h4');
        if (h4 && matcher.titleRx.test(h4.textContent || '')) return allCards[i];
      }
    }
    return null;
  }

  function markCardEarned(card, when){
    if (!card) return;
    card.classList.add('is-earned'); // idempotent
    var actionLink = card.querySelector('.action-link');
    if (actionLink) actionLink.style.display = 'none';
    if (!card.querySelector('.earned-flag')) {
      var flag = document.createElement('span');
      flag.className = 'earned-flag';
      flag.textContent = 'Earned';
      card.insertBefore(flag, card.firstChild);
    }
    if (when && !card.querySelector('.earned-date')) {
      var dateP = document.createElement('p');
      dateP.className = 'earned-date';
      try {
        dateP.textContent = 'Credited ' + new Date(when).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
      } catch (e) { dateP.textContent = 'Credited'; }
      card.appendChild(dateP);
    }
  }

  function markEarnedActions(){
    getMemberPromise().then(function (member) {
      if (!member) return null;

      // Implicit earned: any enrolled member has earned the join welcome
      // bonus by definition. Use the 3-layer lookup so this still works
      // even if the live block hasn't been re-saved with html_id yet.
      var joinMatcher = ACTION_MATCHERS[0]; // 'signup'
      var joinCard = findEarnCard(joinMatcher);
      var joinWhen = member.account_activated_at || member.enrolled_at || member.created_at;
      if (joinCard) markCardEarned(joinCard, joinWhen);

      var R = window.RivoJS;
      var getLogs = R && (R.getCustomerPointsLogs || R.getCustomerActivity || R.getEarningHistory);
      if (typeof getLogs !== 'function') {
        console.log('[Rivo] no points-log API exposed — graceful fallback active (join card stays marked earned)');
        return null;
      }
      return getLogs.call(R);
    }).then(function (resp) {
      if (!resp) return;
      // Accept Array OR { points_logs | logs | activity | data: Array }
      var logs = Array.isArray(resp) ? resp
               : (resp && Array.isArray(resp.points_logs) ? resp.points_logs
               : (resp && Array.isArray(resp.logs) ? resp.logs
               : (resp && Array.isArray(resp.activity) ? resp.activity
               : (resp && Array.isArray(resp.data) ? resp.data : []))));
      if (!logs.length) {
        console.log('[Rivo] markEarnedActions — no log entries returned');
        return;
      }

      var matchedCount = 0;
      logs.forEach(function (log) {
        var note = (log.external_note || '').toString();
        if (!note) return;
        if (/manual/i.test(note)) return; // skip admin point top-ups

        var matcher = null;
        for (var i = 0; i < ACTION_MATCHERS.length; i++) {
          if (ACTION_MATCHERS[i].noteRx.test(note)) { matcher = ACTION_MATCHERS[i]; break; }
        }
        if (!matcher) return;

        var card = findEarnCard(matcher);
        if (!card) {
          console.warn('[Rivo] matched note "' + note + '" but no card found for action:', matcher.name);
          return;
        }
        var alreadyEarned = card.classList.contains('is-earned');
        markCardEarned(card, log.applied_at || log.created_at || log.earned_at || log.date);
        if (!alreadyEarned) matchedCount++;
      });

      console.log('[Rivo] markEarnedActions — flipped ' + matchedCount + ' card(s) earned from ' + logs.length + ' log entries.');
    }).catch(function (err) {
      console.warn('[Rivo] markEarnedActions failed:', err);
    });
  }

  if (window.RivoJS) {
    console.log('[Rivo] RivoJS already present');
    init();
  }
  document.addEventListener('rivo-js-loaded', function(){
    console.log('[Rivo] rivo-js-loaded event received');
    init();
  });
  (function poll(attempts){
    attempts = attempts || 0;
    if (didInit) return;
    if (window.RivoJS) { console.log('[Rivo] RivoJS detected by polling after ' + (attempts * 250) + 'ms'); init(); return; }
    if (attempts >= 60) {
      console.warn('[Rivo] RivoJS never loaded after 15s. Check:');
      console.warn('  · Rivo app installed on the store');
      console.warn('  · JS API enabled in Rivo admin → Developers → Javascript API');
      console.warn('  · Customer logged in (guests will not get personalised data)');
      console.warn('  · Browser console for any blocked-script errors');
      return;
    }
    setTimeout(function(){ poll(attempts + 1); }, 250);
  })();

  /* ---------- Catalog filter chips ----------
     When a non-"all" chip is active, mark the catalog section .is-filtered
     so the show-6 cap and toggle button get hidden by CSS. */
  document.addEventListener('click', function(e){
    var chip = e.target.closest('.catalog .filter .chip');
    if (!chip) return;
    var chips = chip.parentElement.querySelectorAll('.chip');
    chips.forEach(function(c){ c.classList.remove('is-active'); });
    chip.classList.add('is-active');
    var f = chip.dataset.filter || 'all';
    document.querySelectorAll('#rivo-rewards-grid .reward').forEach(function(card){
      card.style.display = (f === 'all' || card.dataset.type === f) ? '' : 'none';
    });
    var catSection = document.getElementById('sec-catalog');
    if (catSection) catSection.classList.toggle('is-filtered', f !== 'all');
  });

  /* ---------- FAQ accordion: open one at a time ---------- */
  document.addEventListener('toggle', function(e){
    if (!e.target.matches('.faq details')) return;
    if (e.target.open) {
      document.querySelectorAll('.faq details').forEach(function(o){
        if (o !== e.target) o.open = false;
      });
    }
  }, true);

  /* ---------- First-time welcome banner — × dismiss + localStorage gate ----------
     Server-side Liquid in members-edit-hero.liquid decides whether the
     banner renders (URL flag OR customer.created_at within 24h). The
     client-side gate below is the third condition: a per-customer dismiss
     stored in localStorage. Once dismissed, the banner stays hidden on
     every subsequent visit until the localStorage key is cleared. */
  (function welcomeBannerDismiss(){
    var banner = document.getElementById('welcome-banner');
    if (!banner) return;
    var cid = banner.getAttribute('data-customer-id') || 'guest';
    var key = 'pm_welcome_dismissed:' + cid;

    if (localStorage.getItem(key) === '1') {
      banner.style.display = 'none';
      return;
    }
    var close = banner.querySelector('.close');
    if (!close) return;
    close.addEventListener('click', function(){
      try { localStorage.setItem(key, '1'); } catch(e){}
      banner.style.maxHeight = banner.offsetHeight + 'px';
      requestAnimationFrame(function(){
        banner.style.transition = 'max-height .35s ease, opacity .35s ease, padding .35s ease';
        banner.style.maxHeight = '0';
        banner.style.opacity = '0';
        banner.style.padding = '0 var(--gutter)';
        banner.style.overflow = 'hidden';
        setTimeout(function(){ banner.remove(); }, 400);
      });
    });
  })();

  /* ---------- Points-pill pulse on first visit ----------
     Liquid sets document.body.dataset.welcome = '1' when show_welcome is
     true on the members-edit page. We add `.is-fresh` to the header pill
     so it pulses twice (CSS keyframes in pm-design-system.css). */
  (function navPointsFreshPulse(){
    if (document.body.dataset.welcome !== '1') return;
    var pill = document.getElementById('rivo-nav-points');
    if (pill) pill.classList.add('is-fresh');
  })();
})();

/* =====================================================================
   PHASE 1 — EARN CELEBRATION (PMCelebrate API + queue + auto-fire)
   ===================================================================== */
(function(){
  var TIER_THRESHOLDS = { Silver: 2000, Gold: 3500 };
  var queue = [];
  var busy  = false;

  function fmt(n){ try { return Number(n || 0).toLocaleString('en-IE'); } catch(e){ return String(n || 0); } }

  function readBalance(){
    var el = document.querySelector('.rivo-points-balance');
    if (!el) return 0;
    var raw = (el.textContent || '0').replace(/[^\d]/g, '');
    return parseInt(raw, 10) || 0;
  }

  function animateCount(from, to, duration){
    var els = document.querySelectorAll('.rivo-points-balance');
    if (!els.length) return;
    var start = performance.now();
    function step(now){
      var t = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      var v = Math.round(from + (to - from) * eased);
      els.forEach(function(el){ el.textContent = fmt(v); });
      if (t < 1) requestAnimationFrame(step);
      else {
        els.forEach(function(el){
          el.classList.add('is-pulse');
          setTimeout(function(){ el.classList.remove('is-pulse'); }, 700);
        });
      }
    }
    requestAnimationFrame(step);
  }

  function updateTierProgress(newTotal){
    var fill = document.getElementById('rivo-fill');
    if (!fill) return;
    var bottom = TIER_THRESHOLDS.Silver;
    var top    = TIER_THRESHOLDS.Gold;
    var pct    = Math.max(0, Math.min(100, ((newTotal - bottom) / (top - bottom)) * 100));
    fill.style.width = pct.toFixed(1) + '%';

    var next = document.getElementById('rivo-next');
    if (next) {
      var remaining = Math.max(0, top - newTotal);
      if (remaining > 0) {
        next.innerHTML = 'Earn <strong>' + fmt(remaining) + '</strong> more points to unlock <strong>Gold Member</strong>.';
      } else {
        next.innerHTML = "You've unlocked <strong>Gold Member</strong>. Welcome to the top tier.";
      }
    }
  }

  function prependActivity(points, reason){
    var list = document.getElementById('rivo-activity-list');
    if (!list) return;
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    var today  = new Date();
    var row    = document.createElement('div');
    row.className = 'row is-new';
    row.innerHTML =
      '<span class="d">' + today.getDate() + ' ' + months[today.getMonth()] + '</span>' +
      '<div class="desc"><strong>' + (reason || 'Points earned') + '</strong><span>Just now</span></div>' +
      '<span class="typ earned">Earned</span>' +
      '<span class="pts">+' + fmt(points) + '</span>';
    list.insertBefore(row, list.firstChild);
  }

  function burstConfetti(){
    var host = document.getElementById('earn-confetti');
    if (!host) return;
    host.innerHTML = '';
    var COUNT = 14;
    var w  = window.innerWidth;
    var ox = w > 600 ? w - 100 : w / 2;
    var oy = w > 600 ? 110 : window.innerHeight - 120;
    for (var i = 0; i < COUNT; i++) {
      var s  = document.createElement('span');
      var dx = (Math.random() - 0.5) * 280;
      s.style.left = ox + 'px';
      s.style.top  = oy + 'px';
      s.style.setProperty('--dx', dx + 'px');
      s.style.animationDelay = (Math.random() * 240) + 'ms';
      host.appendChild(s);
    }
    host.classList.remove('is-burst');
    void host.offsetWidth;
    host.classList.add('is-burst');
    setTimeout(function(){ host.classList.remove('is-burst'); host.innerHTML = ''; }, 2400);
  }

  function showToast(points, reason, detail){
    var toast    = document.getElementById('earn-toast');
    var amtEl    = document.getElementById('earn-toast-amt');
    var reasonEl = document.getElementById('earn-toast-reason');
    var detailEl = document.getElementById('earn-toast-detail');
    if (!toast || !amtEl) return;
    amtEl.textContent = '+' + fmt(points);
    if (reasonEl) reasonEl.textContent = reason || 'Points credited to your account.';
    if (detailEl) {
      if (detail) { detailEl.textContent = detail; detailEl.style.display = ''; }
      else        { detailEl.textContent = ''; detailEl.style.display = 'none'; }
    }
    toast.classList.remove('is-show');
    void toast.offsetWidth;
    toast.classList.add('is-show');
    clearTimeout(toast.__hideT);
    toast.__hideT = setTimeout(hideToast, 5500);
  }
  function hideToast(){
    var toast = document.getElementById('earn-toast');
    if (toast) toast.classList.remove('is-show');
    busy = false;
    setTimeout(drainQueue, 380);
  }
  function drainQueue(){
    if (busy || !queue.length) return;
    busy = true;
    runCelebration(queue.shift());
  }

  function runCelebration(opts){
    var points  = parseInt(opts.points, 10) || 0;
    var reason  = opts.reason || 'Points earned';
    var detail  = opts.detail || '';
    var current = readBalance();
    var target  = current + points;
    animateCount(current, target, 1100);
    updateTierProgress(target);
    prependActivity(points, reason);
    burstConfetti();
    showToast(points, reason, detail);
  }

  window.PMCelebrate = function(opts){
    if (!opts || typeof opts !== 'object') return;
    queue.push(opts);
    drainQueue();
  };

  function wireToastClose(){
    var closeBtn = document.getElementById('earn-toast-close');
    if (closeBtn) closeBtn.addEventListener('click', hideToast);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireToastClose);
  } else { wireToastClose(); }

  // Demo trigger — ?earn-demo=500&earn-reason=Test
  try {
    var params = new URLSearchParams(window.location.search);
    var amt    = parseInt(params.get('earn-demo'), 10);
    if (amt && amt > 0) {
      var reason = params.get('earn-reason') || 'Demo earn — points credited.';
      setTimeout(function(){ window.PMCelebrate({ points: amt, reason: reason }); }, 1200);
    }
  } catch(e){ /* swallow */ }

  // AUTO-FIRE on [data-earn-points] — any clickable card with the attrs
  // fires PMCelebrate on click. Marks is-earned after 1.4s so re-clicks
  // (and re-navigations) don't re-trigger the celebration in the same session.
  document.addEventListener('click', function(e){
    var el = e.target.closest('[data-earn-points]');
    if (!el) return;
    if (el.dataset.earnFired === '1') return;
    if (el.hasAttribute('data-birthday-open')) return;  // modal-driven
    if (el.hasAttribute('data-refer-open'))    return;  // modal-driven
    if (el.hasAttribute('data-earn-action'))   return;  // Rivo-backed (SOCIAL FOLLOW IIFE owns the celebration)
    if (el.classList.contains('is-earned'))    return;  // already credited
    var pts = parseInt(el.dataset.earnPoints, 10) || 0;
    if (pts <= 0) return;
    var reason = el.dataset.earnReason || 'Points credited';
    var detail = el.dataset.earnDetail || '';
    el.dataset.earnFired = '1';
    setTimeout(function(){
      el.classList.add('is-earned');
      var actionLink = el.querySelector('.action-link');
      if (actionLink) actionLink.style.display = 'none';
      if (!el.querySelector('.earned-flag')) {
        var flag = document.createElement('span');
        flag.className = 'earned-flag';
        flag.textContent = 'Earned';
        el.insertBefore(flag, el.firstChild);
      }
    }, 1400);
    window.PMCelebrate({ points: pts, reason: reason, detail: detail });
  });
})();

/* =====================================================================
   PHASE 2 — Show-6 / Show-all toggles (earn + catalog)
   ===================================================================== */
(function(){
  function wireSectionToggle(opts){
    var section = document.getElementById(opts.sectionId);
    var btn     = document.getElementById(opts.btnId);
    if (!section || !btn || btn.dataset.wired === '1') return;
    var label   = btn.querySelector(opts.labelSel);
    var total   = section.querySelectorAll(opts.childSel).length;
    if (label && total) label.textContent = 'Show all ' + total + ' ' + opts.noun;
    btn.addEventListener('click', function(){
      var collapsed = section.classList.toggle('is-collapsed');
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      if (label) label.textContent = collapsed ? 'Show all ' + total + ' ' + opts.noun : 'Show fewer ' + opts.noun;
      if (collapsed) {
        var top = section.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    });
    btn.dataset.wired = '1';
  }
  function wireAll(){
    wireSectionToggle({ sectionId:'sec-earn',    btnId:'earn-toggle',    labelSel:'.earn-toggle-label',    childSel:'.grid > .e',      noun:'ways to earn' });
    wireSectionToggle({ sectionId:'sec-catalog', btnId:'catalog-toggle', labelSel:'.catalog-toggle-label', childSel:'.grid > .reward', noun:'rewards' });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireAll);
  else wireAll();
})();

/* =====================================================================
   PHASE 2.5 — Birthday countdown chip
   When the customer has a `dob` saved in Rivo, decorate every
   [data-birthday-open] earn card with a "N DAYS" pill so they can see
   when their next birthday bonus will fire.
   ===================================================================== */
(function(){
  function daysUntilNextBirthday(dobStr){
    if (!dobStr || typeof dobStr !== 'string') return null;
    /* Rivo stores dob as either MM-DD or YYYY-MM-DD. Pull month + day only. */
    var m, d;
    var iso = dobStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var md  = dobStr.match(/^(\d{2})-(\d{2})$/);
    if (iso) { m = parseInt(iso[2], 10); d = parseInt(iso[3], 10); }
    else if (md) { m = parseInt(md[1], 10); d = parseInt(md[2], 10); }
    else return null;
    if (!m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;

    var now = new Date();
    var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var next = new Date(now.getFullYear(), m - 1, d);
    if (next < today) next = new Date(now.getFullYear() + 1, m - 1, d);
    var diffMs = next - today;
    var diffDays = Math.round(diffMs / 86400000);
    return diffDays;
  }

  function paintChip(card, days){
    if (!card || days == null) return;
    /* Idempotent — don't double-render on re-hydrate. */
    var existing = card.querySelector('.bday-countdown-chip');
    if (existing) existing.remove();

    var chip = document.createElement('span');
    chip.className = 'bday-countdown-chip';
    var label = days === 0
      ? "TODAY"
      : (days === 1 ? "1 DAY" : days + " DAYS");
    chip.innerHTML =
      '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8">' +
        '<circle cx="12" cy="12" r="9"/>' +
        '<path d="M12 7v5l3 2"/>' +
      '</svg>' +
      '<span>' + label + '</span>';
    chip.title = days === 0
      ? 'Your birthday is today — points credit during the next Rivo cron run.'
      : 'Your next birthday is in ' + days + ' day' + (days === 1 ? '' : 's') + '. Rivo will credit your bonus on the day.';
    card.appendChild(chip);
  }

  function paintAllChips(dobStr){
    var days = daysUntilNextBirthday(dobStr);
    if (days == null) return;
    document.querySelectorAll('[data-birthday-open]').forEach(function(card){
      paintChip(card, days);
    });
  }

  /* Walk every nested key looking for a plausible birthday value.
     Rivo's getCustomerDetails response shape varies by version — `dob`,
     `birthday`, `date_of_birth`, `birth_date`, `birth_day`, etc. The
     value can also live on the root, on .customer, or on .user. */
  function readDobFromCustomer(c){
    if (!c) return null;
    var keys = ['dob', 'birthday', 'date_of_birth', 'birth_date', 'birthDate', 'dobMmDd', 'dob_mm_dd'];
    var roots = [c, c.customer, c.user, c.profile, c.data];
    for (var r = 0; r < roots.length; r++) {
      var root = roots[r];
      if (!root) continue;
      for (var k = 0; k < keys.length; k++) {
        var val = root[keys[k]];
        if (val && typeof val === 'string' && val.length >= 5) return val;
      }
    }
    return null;
  }

  var __initRan = false;
  function init(){
    if (__initRan) return;
    if (!window.RivoJS || typeof window.RivoJS.getCustomerDetails !== 'function') {
      console.log('[Birthday countdown] RivoJS not ready yet — waiting for rivo-js-loaded event.');
      return;
    }
    __initRan = true;
    window.RivoJS.getCustomerDetails().then(function(resp){
      console.log('[Birthday countdown] getCustomerDetails raw response:', resp);
      var c = (resp && (resp.customer || resp.user || resp)) || null;
      var dob = readDobFromCustomer(c);
      console.log('[Birthday countdown] resolved dob:', dob);
      if (dob) {
        var days = daysUntilNextBirthday(dob);
        console.log('[Birthday countdown] days until next birthday:', days);
        paintAllChips(dob);
      } else {
        console.log('[Birthday countdown] no dob found on customer — chip not rendered.');
      }
    }).catch(function(err){
      console.warn('[Birthday countdown] getCustomerDetails failed:', err);
      __initRan = false;  /* allow retry */
    });
  }

  /* Try every plausible kick-off point — DOM ready, Rivo loaded event,
     existing-Rivo immediate, plus a final 5-second safety poll for any
     Rivo plan that loads asynchronously without firing the event. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('rivo-js-loaded', init);
  /* Safety net: poll every 500ms for up to 10s. Bails as soon as init runs. */
  var pollTries = 0;
  var pollId = setInterval(function(){
    if (__initRan || pollTries > 20) { clearInterval(pollId); return; }
    pollTries++;
    if (window.RivoJS && typeof window.RivoJS.getCustomerDetails === 'function') init();
  }, 500);

  /* Re-paint after the user just saved a birthday in the modal. */
  document.addEventListener('click', function(e){
    var trigger = e.target && e.target.closest && e.target.closest('#bday-modal .bday-modal__submit, #bday-modal button[type="submit"]');
    if (!trigger) return;
    __initRan = false;
    setTimeout(init, 3000);
  });
})();

/* =====================================================================
   PHASE 3 — Birthday modal (Klaviyo + tier-aware bonus)
   ===================================================================== */
(function(){
  function init(){
    var modal     = document.getElementById('bday-modal');
    var closeBtn  = document.getElementById('bday-modal-close');
    var form      = document.getElementById('bday-modal-form');
    var daySel    = document.getElementById('bday-day');
    var monthSel  = document.getElementById('bday-month');
    var yearSel   = document.getElementById('bday-year');
    var submit    = document.getElementById('bday-modal-submit');
    var statusEl  = document.getElementById('bday-modal-status');
    var triggers  = document.querySelectorAll('[data-birthday-open]');
    if (!modal || !triggers.length) return;

    // Populate day 01–31
    if (daySel && daySel.children.length <= 1) {
      for (var d = 1; d <= 31; d++) {
        var opt = document.createElement('option');
        var dd  = (d < 10 ? '0' : '') + d;
        opt.value = dd; opt.textContent = dd;
        daySel.appendChild(opt);
      }
    }
    // Populate year — current - 16 down to -100
    if (yearSel && yearSel.children.length <= 1) {
      var nowY = new Date().getFullYear();
      for (var y = nowY - 16; y >= nowY - 100; y--) {
        var opt2 = document.createElement('option');
        opt2.value = String(y); opt2.textContent = String(y);
        yearSel.appendChild(opt2);
      }
    }

    function openModal(e){
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (statusEl) { statusEl.textContent = ''; statusEl.className = 'bday-modal__status'; }
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
    }
    function closeModal(){
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
    }

    triggers.forEach(function(t){ t.addEventListener('click', openModal); });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function(e){ if (e.target === modal) closeModal(); });
    document.addEventListener('keydown', function(e){
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    if (!form) return;
    form.addEventListener('submit', function(e){
      e.preventDefault();
      var day   = daySel ? daySel.value : '';
      var month = monthSel ? monthSel.value : '';
      var year  = yearSel ? yearSel.value : '';
      if (!day || !month || !year) {
        statusEl.textContent = 'Please pick a day, month, and year.';
        statusEl.className = 'bday-modal__status is-error';
        return;
      }
      var monthNames = ['','January','February','March','April','May','June','July','August','September','October','November','December'];
      var monthName  = monthNames[parseInt(month, 10)];
      var dayInt     = parseInt(day, 10);
      var ordinal    = (dayInt % 10 === 1 && dayInt !== 11) ? 'st'
                     : (dayInt % 10 === 2 && dayInt !== 12) ? 'nd'
                     : (dayInt % 10 === 3 && dayInt !== 13) ? 'rd' : 'th';

      submit.disabled = true;
      submit.textContent = 'Saving…';
      statusEl.textContent = '';
      statusEl.className = 'bday-modal__status';

      // Klaviyo wants the full ISO date (for age-band segmentation).
      // Rivo only accepts MM-DD (no year). https://developer-docs.rivo.io/docs/updatecustomerdob
      var bdayISO  = year + '-' + month + '-' + day; // YYYY-MM-DD
      var bdayMMDD = month + '-' + day;              // MM-DD

      // ---- WRITE 1: Klaviyo ($birthday property for marketing flows) ----
      var klaviyoOk = false;
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify({ '$birthday': bdayISO });
          klaviyoOk = true;
        } else if (window._learnq && window._learnq.push) {
          window._learnq.push(['identify', { '$birthday': bdayISO }]);
          klaviyoOk = true;
        }
      } catch(err) { console.warn('[Birthday] Klaviyo write failed:', err); }

      // ---- WRITE 2: Rivo dob via the documented official API ----
      // https://developer-docs.rivo.io/docs/updatecustomerdob
      var rivoPromise = (window.RivoJS && typeof window.RivoJS.updateCustomerDob === 'function')
        ? window.RivoJS.updateCustomerDob(bdayMMDD)
        : Promise.reject(new Error('RivoJS.updateCustomerDob not available'));

      rivoPromise.then(function(resp){
        // Rivo's updateCustomerDob resolves with a bare `true` on success
        // (not an object), so we accept any truthy resolved value.
        var rivoOk = resp === true
          || !!(resp && (resp.success === true || resp.data || resp.id || resp.dob));
        finishBirthday(rivoOk);
      }).catch(function(err){
        console.warn('[Birthday] Rivo updateCustomerDob failed:', err);
        finishBirthday(false);
      });

      function finishBirthday(rivoOk){
        submit.disabled = false;
        if (rivoOk || klaviyoOk) {
          submit.textContent = 'Saved ✓';
          statusEl.textContent = 'See you on ' + monthName + ' ' + dayInt + ordinal + " — we'll celebrate with you.";
          statusEl.className = 'bday-modal__status is-success';
          console.log('[Birthday] writes:', { klaviyo: klaviyoOk, rivo: rivoOk });
          if (!rivoOk) console.warn('[Birthday] Klaviyo saved but Rivo dob write failed — 150-point bonus will not fire.');
          if (!klaviyoOk) console.warn('[Birthday] Rivo saved but Klaviyo $birthday write failed — flows keyed off $birthday will not run.');
          setTimeout(function(){
            closeModal();
            submit.textContent = 'Save birthday';
            /* No PMCelebrate fire on save — points credit on the actual
               birthday via Rivo's native "Celebrate a Birthday" rule.
               Showing a +150 toast here would misrepresent that the
               balance changed when it hasn't. */
          }, 2200);
        } else {
          submit.textContent = 'Save birthday';
          statusEl.textContent = "We couldn't save your birthday just now. Please try again.";
          statusEl.className = 'bday-modal__status is-error';
        }
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   PHASE 4 — Refer share modal (copy + 6 social buttons)
   ===================================================================== */
(function(){
  function init(){
    var modal      = document.getElementById('share-modal');
    var modalClose = document.getElementById('share-modal-close');
    var modalUrl   = document.getElementById('share-modal-url');
    var modalCopy  = document.getElementById('share-modal-copy');
    var triggers   = document.querySelectorAll('[data-refer-open]');
    if (!modal || !triggers.length) return;

    var defaultCode = (modalUrl && modalUrl.value) ? modalUrl.value : '';
    var defaultUrl  = defaultCode && defaultCode.indexOf('http') === 0
      ? defaultCode
      : (defaultCode ? ('https://pestleandmortar.com/?r=' + defaultCode) : 'https://pestleandmortar.com/');
    var refUrl = defaultUrl;
    var shareMsg = "I thought you might like Pestle & Mortar. Here's €10 off your first order:";

    function buildLinks(url){
      var u = encodeURIComponent(url);
      var msg = encodeURIComponent(shareMsg + ' ' + url);
      var subj = encodeURIComponent('€10 off Pestle & Mortar');
      var bodyEmail = encodeURIComponent(shareMsg + '\n\n' + url + '\n\n— Sent via The Members Edit');
      var byId = function(id){ return document.getElementById(id); };
      if (byId('share-whatsapp'))  byId('share-whatsapp').href  = 'https://wa.me/?text=' + msg;
      if (byId('share-facebook'))  byId('share-facebook').href  = 'https://www.facebook.com/sharer/sharer.php?u=' + u;
      if (byId('share-twitter'))   byId('share-twitter').href   = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(shareMsg) + '&url=' + u;
      if (byId('share-messenger')) byId('share-messenger').href = 'https://www.facebook.com/dialog/send?link=' + u + '&app_id=291494419107518&redirect_uri=' + u;
      if (byId('share-email'))     byId('share-email').href     = 'mailto:?subject=' + subj + '&body=' + bodyEmail;
      if (byId('share-sms'))       byId('share-sms').href       = 'sms:?&body=' + msg;
    }

    function refreshFromRivo(){
      // Pull the live referral code from the page (hydrated by Rivo)
      var liveCode = document.getElementById('rivo-ref-url');
      if (liveCode && liveCode.value) {
        var v = liveCode.value.trim();
        refUrl = v.indexOf('http') === 0 ? v : ('https://pestleandmortar.com/?r=' + v);
      }
      var liveUrlSpan = document.getElementById('rivo-ref-msg-url');
      if (liveUrlSpan && liveUrlSpan.textContent) {
        var t = liveUrlSpan.textContent.trim();
        if (t.indexOf('?r=') !== -1) refUrl = (t.indexOf('http') === 0 ? t : ('https://' + t));
      }
      if (modalUrl) modalUrl.value = refUrl;
      buildLinks(refUrl);
    }

    function openModal(e){
      if (e) { e.preventDefault(); e.stopPropagation(); }
      refreshFromRivo();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
    }
    function closeModal(){
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
    }

    triggers.forEach(function(t){ t.addEventListener('click', openModal); });
    if (modalClose) modalClose.addEventListener('click', closeModal);
    modal.addEventListener('click', function(e){ if (e.target === modal) closeModal(); });
    document.addEventListener('keydown', function(e){
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    if (modalCopy && modalUrl) {
      modalCopy.addEventListener('click', function(){
        modalUrl.select();
        modalUrl.setSelectionRange(0, 99999);
        var copied = false;
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(modalUrl.value); copied = true;
          } else {
            document.execCommand('copy'); copied = true;
          }
        } catch(err){ copied = false; }
        if (copied) {
          var orig = modalCopy.textContent;
          modalCopy.textContent = 'Copied ✓';
          modalCopy.classList.add('is-copied');
          setTimeout(function(){
            modalCopy.textContent = orig;
            modalCopy.classList.remove('is-copied');
          }, 1800);
        }
      });
    }

    buildLinks(refUrl);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* OKENDO REVIEW → RIVO bridge moved to assets/pm-review-bridge.js
   so it can be loaded on every PDP (where the Okendo widget lives).
   See sections/pdp-v2-reviews.liquid for the include. */

/* ============================================================
   CATALOG REDEEM — delegated click handler on #rivo-rewards-grid
   https://developer-docs.rivo.io/docs/redeemreward
   Any .reward card with data-reward-id becomes redeemable.
   ============================================================ */
(function(){
  var grid = document.getElementById('rivo-rewards-grid');
  if (!grid) return;

  grid.addEventListener('click', function(e){
    var btn = e.target.closest('.redeem');
    if (!btn || !grid.contains(btn)) return;
    var card = btn.closest('.reward');
    if (!card) return;
    if (card.classList.contains('locked')) return;
    if (card.classList.contains('is-redeemed')) return;
    var rewardId = parseInt(card.dataset.rewardId, 10);
    if (!rewardId) {
      console.warn('[Redeem] card missing data-reward-id', card);
      return;
    }
    if (!window.RivoJS || typeof window.RivoJS.redeemReward !== 'function') {
      console.warn('[Redeem] RivoJS.redeemReward not available');
      return;
    }

    var original = btn.textContent;
    btn.textContent = 'Redeeming…';
    btn.style.pointerEvents = 'none';
    btn.style.opacity = '0.7';

    window.RivoJS.redeemReward(rewardId).then(function(resp){
      console.log('[Rivo] redeemReward response:', resp);
      if (!resp || resp.success !== true) {
        throw new Error('Rivo returned non-success: ' + JSON.stringify(resp));
      }
      var code = resp.points_purchase && resp.points_purchase.code;
      var name = resp.points_purchase && resp.points_purchase.name;

      btn.textContent = 'Redeemed ✓';
      btn.style.background = 'var(--black)';
      btn.style.color = 'var(--bg)';
      card.classList.add('is-redeemed');

      if (typeof resp.points_tally !== 'undefined') {
        document.querySelectorAll('.rivo-points-balance').forEach(function(el){
          el.textContent = Number(resp.points_tally).toLocaleString('en-IE');
        });
        window.__pmPoints = resp.points_tally;
      }

      if (window.PMCelebrate) {
        window.PMCelebrate({
          points: -1 * ((resp.points_purchase && resp.points_purchase.points_amount) || 0),
          reason: 'Redeemed: ' + (name || 'Reward'),
          detail: code ? 'Code: ' + code : 'Applied to your account'
        });
      }

      var msg = document.getElementById('rivo-redeem-msg');
      if (msg) {
        msg.textContent = code
          ? 'Redeemed ' + name + ' — copy code ' + code + ' to use at checkout.'
          : 'Redeemed ' + name + '.';
        msg.classList.remove('is-error');
      }
    }).catch(function(err){
      console.warn('[Redeem] failed:', err);
      btn.textContent = original;
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
      var msg = document.getElementById('rivo-redeem-msg');
      if (msg) {
        msg.textContent = "We couldn't redeem that reward. Please refresh and try again.";
        msg.classList.add('is-error');
      }
    });
  });
})();

/* ============================================================
   WISHLIST — Rivo Favorites API bridge
   Hydrate:  https://developer-docs.rivo.io/docs/getfavoriteproducts
   Add:      https://developer-docs.rivo.io/docs/addfavoriteproduct
   Remove:   https://developer-docs.rivo.io/docs/removefavoriteproduct

   Markup contract (build into a wishlist section template):
     • Grid container:       <div data-wishlist-grid></div>
     • Optional count label: <span class="wish-count">0</span>
     • Add button (PDP):     <button data-wishlist-add data-product-id="...">
     • Remove button (card): <button data-wishlist-remove data-product-id="...">
   All listeners bail cleanly when their target markup is absent, so
   shipping this JS before the wishlist page exists is safe.
   ============================================================ */
(function(){
  function rivoReady(){ return !!(window.RivoJS); }

  // ---- Hydrate on page load ----
  // Fires whenever there's a wishlist grid OR any .wish-count chip on
  // the page (header, dashboard sidebar, PDP-adjacent badges, etc.) so
  // the count stays in sync everywhere.
  (function hydrate(){
    var hasGrid  = !!document.querySelector('[data-wishlist-grid]');
    var hasCount = !!document.querySelector('.wish-count');
    if (!hasGrid && !hasCount) return;
    function run(){
      if (!rivoReady() || typeof window.RivoJS.getFavoriteProducts !== 'function') return false;
      window.RivoJS.getFavoriteProducts().then(function(resp){
        console.log('[Wishlist] getFavoriteProducts:', resp);
        if (!resp || resp.success !== true) return;
        var products = resp.products || [];
        if (typeof window.renderWishlist === 'function') {
          window.renderWishlist(products);
        } else {
          // No custom renderer (e.g. on dashboard) — at minimum sync the
          // count chips so the sidebar / header label is accurate.
          document.querySelectorAll('.wish-count').forEach(function(el){
            el.textContent = products.length;
          });
        }
      }).catch(function(err){
        console.warn('[Wishlist] hydration failed:', err);
      });
      return true;
    }
    if (run()) return;
    // RivoJS may load after this IIFE — poll briefly, then give up.
    var tries = 0;
    var t = setInterval(function(){
      tries++;
      if (run() || tries > 60) clearInterval(t);
    }, 250);
  })();

  // ---- Add to wishlist (PDP heart, etc.) ----
  document.addEventListener('click', function(e){
    var btn = e.target.closest('[data-wishlist-add]');
    if (!btn) return;
    var productId = parseInt(btn.dataset.productId, 10);
    if (!productId) return;
    if (!rivoReady() || typeof window.RivoJS.addFavoriteProduct !== 'function') {
      console.warn('[Wishlist] RivoJS.addFavoriteProduct not available');
      return;
    }
    btn.classList.add('is-loading');
    window.RivoJS.addFavoriteProduct(productId).then(function(resp){
      console.log('[Wishlist] addFavoriteProduct:', resp);
      btn.classList.remove('is-loading');
      if (resp && resp.success) {
        btn.classList.add('is-added');
        if (window.PMCelebrate) {
          window.PMCelebrate({
            points: 0,
            reason: 'Saved to wishlist',
            detail: 'View your wishlist any time from your account.'
          });
        }
      }
    }).catch(function(err){
      console.warn('[Wishlist] add failed:', err);
      btn.classList.remove('is-loading');
    });
  });

  // ---- Remove from wishlist (× on each card) ----
  document.addEventListener('click', function(e){
    var btn = e.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    var productId = parseInt(btn.dataset.productId, 10);
    if (!productId) return;
    if (!rivoReady() || typeof window.RivoJS.removeFavoriteProduct !== 'function') {
      console.warn('[Wishlist] RivoJS.removeFavoriteProduct not available');
      return;
    }
    var card = btn.closest('.wcard') || btn.closest('[data-wishlist-card]');
    window.RivoJS.removeFavoriteProduct(productId).then(function(resp){
      console.log('[Wishlist] removeFavoriteProduct:', resp);
      if (resp && resp.success && card) {
        card.style.transition = 'opacity 0.3s ease';
        card.style.opacity = '0';
        setTimeout(function(){
          card.remove();
          var countEl = document.querySelector('.wish-count');
          if (countEl) {
            var n = parseInt(countEl.textContent, 10) || 0;
            countEl.textContent = Math.max(0, n - 1);
          }
        }, 300);
      }
    }).catch(function(err){
      console.warn('[Wishlist] remove failed:', err);
    });
  });
})();

/* ============================================================
   SOCIAL FOLLOW DEEP-LINKING — on mobile, try the native app first
   ([data-app-link]) and fall back to the web URL ([href]) after a
   short timeout if the app didn't open. Desktop falls through to
   the plain href. Runs in capture phase so it fires before the
   credit handler below.
   ============================================================ */
(function(){
  var IS_MOBILE = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
  if (!IS_MOBILE) return;

  document.addEventListener('click', function(e){
    var card = e.target.closest('[data-app-link]');
    if (!card) return;
    var appUrl = card.getAttribute('data-app-link');
    var webUrl = card.getAttribute('href');
    if (!appUrl || !webUrl) return;
    e.preventDefault();

    // Fallback to the web URL if the app scheme silently fails.
    var fallbackTimer = setTimeout(function(){
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }, 600);

    // Cancel the fallback if the OS actually switched apps (page
    // backgrounds → visibilitychange / pagehide fires).
    var cancel = function(){
      clearTimeout(fallbackTimer);
      document.removeEventListener('visibilitychange', cancel);
      window.removeEventListener('pagehide', cancel);
    };
    document.addEventListener('visibilitychange', cancel);
    window.addEventListener('pagehide', cancel);

    // Hidden-iframe pattern — modern iOS Safari blocks a direct
    // window.location = 'instagram://...' for unknown schemes, but
    // an iframe src silently fails when the app isn't installed.
    try {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = appUrl;
      document.body.appendChild(iframe);
      setTimeout(function(){ if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1000);
    } catch (_) {
      window.location.href = appUrl;
    }
  }, true); // capture phase: runs before credit handler
})();

/* ============================================================
   SOCIAL FOLLOW CREDIT — wire cards with [data-earn-action] to
   fire Rivo's custom action via the documented proxy URL.
   Cards open in a new tab; the fetch is fire-and-forget so the
   external page loads without waiting for the credit to land.
   https://developer-docs.rivo.io/docs/trigger-custom-actions-with-javascript
   ============================================================ */
(function(){
  function recordAction(actionName){
    if (!window.Rivo || !window.Rivo.global_config || !window.Rivo.common || !window.Rivo.common.customer) {
      console.warn('[Social] Rivo runtime not loaded — credit skipped for:', actionName);
      return Promise.reject(new Error('Rivo not loaded'));
    }
    var proxyUrl = window.Rivo.global_config.proxy_paths.loy
                 + '/loy/customers/' + window.Rivo.common.customer.id
                 + '/record_custom_action';
    return fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ custom_action_name: actionName })
    }).then(function(r){
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json().catch(function(){ return {}; });
    });
  }

  // Delegated so cards injected dynamically (e.g. by Rivo hydration) work too.
  document.addEventListener('click', function(e){
    var card = e.target.closest('.earn .e[data-earn-action]');
    if (!card) return;
    var actionName = card.dataset.earnAction;
    if (!actionName || actionName === 'none') return;

    if (card.classList.contains('is-earned'))   return;  // already credited
    if (card.hasAttribute('data-birthday-open'))return;  // modal-driven
    if (card.hasAttribute('data-refer-open'))   return;  // modal-driven

    // Legacy snake_case keys (signup/skin_quiz/email_sub/sms_sub) are
    // earned-state hints only — markEarnedActions handles those after the
    // base action fires server-side. Any other value is treated as a Rivo
    // custom action name and POSTed via record_custom_action.
    var LEGACY_KEYS = ['signup', 'skin_quiz', 'email_sub', 'sms_sub'];
    if (LEGACY_KEYS.indexOf(actionName) !== -1) return;

    recordAction(actionName).then(function(){
      console.log('[Social] credited:', actionName);
      if (window.PMCelebrate) {
        window.PMCelebrate({
          points: 50,
          reason: actionName,
          detail: '+50 points credited to your account'
        });
      }
    }).catch(function(err){
      console.warn('[Social] credit failed for ' + actionName, err);
    });
  });
})();

/* ============================================================
   REVEAL OBSERVER — runs the [data-reveal] fade-in for elements
   inside .members-edit-* sections. Required on host themes where
   the global observer (e.g. pm-shell.js on staging) is missing or
   scoped to a different namespace (e.g. PNM - New Website scopes
   its observer to `.pnm [data-reveal]`).
   Sets body.pm-has-reveal-observer so the opacity:0 starting state
   in pm-members-edit.css only kicks in when an observer is active —
   prevents elements from getting stuck invisible on themes without
   a compatible observer.
   ============================================================ */
(function(){
  var revealEls = document.querySelectorAll(
    '.shopify-section[class*="members-edit"] [data-reveal], ' +
    '[class*="members-edit-"] [data-reveal]'
  );
  if (!revealEls.length) return;
  document.body.classList.add('pm-has-reveal-observer');
  if (!('IntersectionObserver' in window)) {
    revealEls.forEach(function(el){ el.classList.add('is-in'); });
    return;
  }
  var io = new IntersectionObserver(function(entries){
    entries.forEach(function(e){
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  revealEls.forEach(function(el){ io.observe(el); });
})();

/* =====================================================================
   Account dashboard — REDEEMED COUPONS card
   Lists each Rivo coupon (discount code earned from points redemption)
   with a tap-to-copy button. Probes multiple RivoJS method names since
   the SDK varies across Rivo plans / versions; falls back to a tidy
   empty state if nothing's exposed.
   ===================================================================== */
(function(){
  var CARD_SEL  = '[data-pnm-rivo-coupons-card]';
  var LIST_SEL  = '[data-pnm-rivo-coupons-list]';
  var EMPTY_SEL = '[data-pnm-rivo-coupons-empty]';
  var initRan = false;

  function escapeHtml(s){ return String(s||'').replace(/[&<>"']/g, function(c){
    return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c];
  }); }

  function fmtDate(s){
    if (!s) return '';
    try {
      var d = new Date(s);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch(_) { return ''; }
  }

  function pickArray(resp){
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    var keys = ['coupons','redemptions','rewards','rewards_redeemed','customer_redeemed_rewards','data','records','items'];
    for (var i = 0; i < keys.length; i++) {
      if (Array.isArray(resp[keys[i]])) return resp[keys[i]];
    }
    /* Single-level nesting (e.g. { customer: { coupons: [...] } }) */
    var nestedRoots = [resp.customer, resp.user, resp.profile];
    for (var n = 0; n < nestedRoots.length; n++) {
      var r = nestedRoots[n];
      if (!r) continue;
      for (var j = 0; j < keys.length; j++) {
        if (Array.isArray(r[keys[j]])) return r[keys[j]];
      }
    }
    return [];
  }

  function pickCode(c){ return c.code || c.coupon_code || c.discount_code || c.token || ''; }
  function pickLabel(c){ return c.title || c.name || c.reward_name || c.reward_title || c.label || 'Reward'; }
  function pickExpiry(c){ return c.expires_at || c.expiry_date || c.expires || c.valid_until || c.expires_on || ''; }
  function isUsed(c){
    var status = (c.status || c.state || '').toLowerCase();
    return c.used === true || c.is_used === true || status === 'used' || status === 'redeemed';
  }

  function renderCard(coupons){
    var card = document.querySelector(CARD_SEL);
    if (!card) return;
    var list = card.querySelector(LIST_SEL);
    var empty = card.querySelector(EMPTY_SEL);
    if (!list) return;

    card.hidden = false;

    var active = (coupons || []).filter(function(c){ return pickCode(c) && !isUsed(c); });
    if (active.length === 0) {
      list.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    list.innerHTML = active.map(function(c){
      var code = pickCode(c);
      var label = pickLabel(c);
      var expiry = pickExpiry(c);
      var meta = escapeHtml(label) + (expiry ? ' · expires ' + escapeHtml(fmtDate(expiry) || expiry) : '');
      return ''
        + '<div class="pnm-acc-coupon">'
        +   '<div class="pnm-acc-coupon__main">'
        +     '<div class="pnm-acc-coupon__code">' + escapeHtml(code) + '</div>'
        +     '<div class="pnm-acc-coupon__meta">' + meta + '</div>'
        +   '</div>'
        +   '<button type="button" class="pnm-acc-coupon__copy" data-pnm-coupon-copy="' + escapeHtml(code) + '">Copy</button>'
        + '</div>';
    }).join('');
  }

  function tryFetch(methodName, args){
    if (!window.RivoJS) return Promise.resolve(null);
    var fn = window.RivoJS[methodName];
    if (typeof fn !== 'function') return Promise.resolve(null);
    try {
      var ret = fn.apply(window.RivoJS, args || []);
      return (ret && typeof ret.then === 'function') ? ret : Promise.resolve(ret);
    } catch (e) { return Promise.resolve(null); }
  }

  function fetchCoupons(){
    /* Probe candidate method names in priority order. First non-empty
       response wins. */
    var candidates = [
      'getCustomerCoupons',
      'getCustomerRewards',
      'getRedeemedRewards',
      'getCustomerRedeemedRewards',
      'getCustomerRedemptions',
      'listRedemptions',
      'getCustomerActiveRewards'
    ];
    function step(i){
      if (i >= candidates.length) {
        renderCard([]);  /* Triggers empty state */
        return;
      }
      tryFetch(candidates[i]).then(function(resp){
        if (resp) {
          var arr = pickArray(resp);
          if (arr.length > 0) {
            console.log('[Rivo coupons] using', candidates[i], '— count:', arr.length);
            renderCard(arr);
            return;
          }
        }
        step(i + 1);
      });
    }
    step(0);
  }

  function init(){
    if (initRan) return;
    if (!document.querySelector(CARD_SEL)) return;
    if (!window.RivoJS) return;
    initRan = true;
    fetchCoupons();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('rivo-js-loaded', init);
  var tries = 0;
  var iv = setInterval(function(){
    if (initRan || tries > 20) { clearInterval(iv); return; }
    tries++;
    init();
  }, 500);

  /* Copy-to-clipboard for each coupon row. */
  document.addEventListener('click', function(e){
    var btn = e.target && e.target.closest && e.target.closest('[data-pnm-coupon-copy]');
    if (!btn) return;
    var code = btn.getAttribute('data-pnm-coupon-copy') || '';
    if (!code) return;
    var done = function(){
      var orig = btn.textContent;
      btn.textContent = 'Copied ✓';
      btn.classList.add('is-copied');
      setTimeout(function(){
        btn.textContent = orig;
        btn.classList.remove('is-copied');
      }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, done);
    } else {
      var ta = document.createElement('textarea');
      ta.value = code; ta.style.position='absolute'; ta.style.left='-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch(_){}
      document.body.removeChild(ta);
      done();
    }
  });
})();
